import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  boardLayout, drawCells, drawClueAreas, drawGridLines, prepareCanvas,
} from './nonoDraw';
import {
  deleteColumn, deleteRow, filledShape, flipped, placedCells, resampled, rotated,
} from '../../utils/oneBit';
import { analyze } from '../../utils/nonoSolver';
import GridSizeInputs from './GridSizeInputs';
import { canvasPoint, TracerActions, TracerCanvas, TracerStatus, useTracerKeys } from './tracerShared';

const GUTTER = 16;

// Places a 1-bit icon on the grid cell by cell. The icon moves in whole cells and is resized with
// nearest-neighbour sampling; rows and columns of the icon can be deleted to get 16×16 down to 10×10.
function NonoPixelTracer({ width, height, bitmap: original, maxWidth, onResizeGrid, onApply, onCancel }) {
  const canvasRef = useRef(null);
  const sourceRef = useRef(null);
  const drag = useRef(null);
  const [history, setHistory] = useState([]);
  const [bitmap, setBitmap] = useState(original);
  const [size, setSize] = useState({ w: original.w, h: original.h });
  const [lock, setLock] = useState(true);
  const [pos, setPos] = useState({ x: Math.floor((width - original.w) / 2), y: Math.floor((height - original.h) / 2) });
  const [invert, setInvert] = useState(false);
  const [fill, setFill] = useState(false);
  const [sourceHover, setSourceHover] = useState(null);
  const layout = boardLayout(width, height, maxWidth, 640);

  // A new grid size puts the icon back in the middle.
  useEffect(() => {
    setPos({ x: Math.floor((width - size.w) / 2), y: Math.floor((height - size.h) / 2) });
  }, [width, height]); // eslint-disable-line react-hooks/exhaustive-deps

  const shape = useMemo(() => resampled(fill ? filledShape(bitmap) : bitmap, size.w, size.h), [bitmap, fill, size]);
  const cells = useMemo(() => placedCells(shape, width, height, pos.x, pos.y, invert), [shape, width, height, pos, invert]);
  const analysis = useMemo(() => analyze(width, height, cells), [width, height, cells]);

  useEffect(() => {
    const ctx = prepareCanvas(canvasRef.current, layout);
    drawClueAreas(ctx, layout, analysis.clues, null);
    drawCells(ctx, layout, cells, { unresolved: analysis.status === 'empty' ? null : analysis.unresolvedCells });
    drawGridLines(ctx, layout);
    ctx.strokeStyle = '#2f5bea';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 4]);
    ctx.strokeRect(layout.left + pos.x * layout.cell + 0.5, layout.top + pos.y * layout.cell + 0.5, size.w * layout.cell, size.h * layout.cell);
    ctx.setLineDash([]);
  });

  // The icon itself, magnified, with row and column deleters in the margins.
  const pixel = Math.max(4, Math.min(16, Math.floor(220 / Math.max(bitmap.w, bitmap.h))));
  useEffect(() => {
    const canvas = sourceRef.current;
    const dpr = window.devicePixelRatio || 1;
    const cw = GUTTER + bitmap.w * pixel, ch = GUTTER + bitmap.h * pixel;
    canvas.width = cw * dpr;
    canvas.height = ch * dpr;
    canvas.style.width = `${cw}px`;
    canvas.style.height = `${ch}px`;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cw, ch);
    ctx.fillStyle = '#16181d';
    bitmap.bits.forEach((v, i) => v && ctx.fillRect(GUTTER + (i % bitmap.w) * pixel, GUTTER + Math.floor(i / bitmap.w) * pixel, pixel, pixel));
    ctx.strokeStyle = 'rgba(0,0,0,0.12)';
    ctx.lineWidth = 1;
    ctx.strokeRect(GUTTER + 0.5, GUTTER + 0.5, bitmap.w * pixel, bitmap.h * pixel);
    if (sourceHover) {
      ctx.fillStyle = 'rgba(224, 49, 49, 0.35)';
      if (sourceHover.kind === 'row') ctx.fillRect(0, GUTTER + sourceHover.index * pixel, cw, pixel);
      else ctx.fillRect(GUTTER + sourceHover.index * pixel, 0, pixel, ch);
    }
    ctx.fillStyle = '#999';
    ctx.font = '11px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('×', GUTTER / 2, GUTTER / 2);
  }, [bitmap, pixel, sourceHover]);

  const sourceTarget = (e) => {
    const rect = sourceRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    if (x < GUTTER && y >= GUTTER) {
      const index = Math.floor((y - GUTTER) / pixel);
      if (index < bitmap.h) return { kind: 'row', index };
    }
    if (y < GUTTER && x >= GUTTER) {
      const index = Math.floor((x - GUTTER) / pixel);
      if (index < bitmap.w) return { kind: 'col', index };
    }
    return null;
  };

  const edit = useCallback((next, nextSize) => {
    setHistory(h => [...h, { bitmap, size }]);
    setBitmap(next);
    setSize(nextSize);
  }, [bitmap, size]);

  const removeLine = (target) => {
    if (target.kind === 'row' && bitmap.h > 1) {
      // A box that is still at 1:1 shrinks with the icon; one that was resized keeps its size.
      edit(deleteRow(bitmap, target.index), { w: size.w, h: size.h === bitmap.h ? size.h - 1 : size.h });
    } else if (target.kind === 'col' && bitmap.w > 1) {
      edit(deleteColumn(bitmap, target.index), { w: size.w === bitmap.w ? size.w - 1 : size.w, h: size.h });
    }
    setSourceHover(null);
  };

  const undoEdit = useCallback(() => {
    setHistory(h => {
      if (!h.length) return h;
      const last = h[h.length - 1];
      setBitmap(last.bitmap);
      setSize(last.size);
      return h.slice(0, -1);
    });
  }, []);

  const resizeTo = (w, h, axis) => {
    const nw = Math.max(1, Math.min(60, Math.round(w) || 1));
    const nh = Math.max(1, Math.min(60, Math.round(h) || 1));
    if (lock && axis === 'w') setSize({ w: nw, h: Math.max(1, Math.round((nw * bitmap.h) / bitmap.w)) });
    else if (lock && axis === 'h') setSize({ w: Math.max(1, Math.round((nh * bitmap.w) / bitmap.h)), h: nh });
    else setSize({ w: nw, h: nh });
  };

  const point = (e) => canvasPoint(canvasRef.current, e);

  const onPointerDown = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    drag.current = { p0: point(e), pos0: pos };
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const p = point(e);
    const x = d.pos0.x + Math.round((p.x - d.p0.x) / layout.cell);
    const y = d.pos0.y + Math.round((p.y - d.p0.y) / layout.cell);
    setPos(prev => (prev.x === x && prev.y === y ? prev : { x, y }));
  };

  const apply = useCallback(() => onApply(cells), [onApply, cells]);

  useTracerKeys({
    apply,
    onCancel,
    onKey: (e) => {
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      const moves = { arrowleft: [-1, 0], arrowright: [1, 0], arrowup: [0, -1], arrowdown: [0, 1] };
      if (moves[key] && !mod) {
        e.preventDefault();
        const step = e.shiftKey ? 5 : 1;
        setPos(prev => ({ x: prev.x + moves[key][0] * step, y: prev.y + moves[key][1] * step }));
      } else if (mod && key === 'z') {
        e.preventDefault();
        undoEdit();
      } else if (mod) {
        // leave other shortcuts alone
      } else if (e.key === '+' || e.key === '=') {
        setSize(s => ({ w: Math.min(60, s.w + 1), h: lock ? Math.min(60, Math.max(1, Math.round(((s.w + 1) * bitmap.h) / bitmap.w))) : s.h }));
      } else if (e.key === '-') {
        setSize(s => ({ w: Math.max(1, s.w - 1), h: lock ? Math.max(1, Math.round(((s.w - 1) * bitmap.h) / bitmap.w)) : s.h }));
      } else if (key === 'i') {
        setInvert(v => !v);
      } else if (key === 'f') {
        setFill(v => !v);
      } else if (key === 'r') {
        edit(rotated(bitmap, !e.shiftKey), { w: size.h, h: size.w });
      }
    },
  });

  const scaled = size.w !== bitmap.w || size.h !== bitmap.h;

  return (
    <div className="nn-tracer">
      <TracerCanvas canvasRef={canvasRef} drag={drag} onPointerDown={onPointerDown} onPointerMove={onPointerMove} />
      <div className="nn-tracer-panel">
        <div className="nn-pixel-source">
          <span className="nn-hint">Lähde {bitmap.w}×{bitmap.h}. Poista rivi tai sarake klikkaamalla reunan ×-aluetta.</span>
          <canvas
            ref={sourceRef}
            className="nn-source-canvas"
            onPointerMove={e => setSourceHover(prev => {
              const t = sourceTarget(e);
              return prev?.kind === t?.kind && prev?.index === t?.index ? prev : t;
            })}
            onPointerLeave={() => setSourceHover(null)}
            onClick={e => { const t = sourceTarget(e); if (t) removeLine(t); }}
            style={{ cursor: sourceHover ? 'pointer' : 'default' }}
          />
        </div>
        <div className="nn-row">
          <button className="btn-secondary" disabled={!history.length} onClick={undoEdit}>Kumoa <kbd>Ctrl+Z</kbd></button>
          <button className="nn-tool icon" onClick={() => edit(rotated(bitmap, false), { w: size.h, h: size.w })} title="Kierrä 90° vastapäivään"><span className="material-symbols-outlined ui-icon">rotate_left</span></button>
          <button className="nn-tool icon" onClick={() => edit(rotated(bitmap, true), { w: size.h, h: size.w })} title="Kierrä 90° myötäpäivään (R)"><span className="material-symbols-outlined ui-icon">rotate_right</span></button>
          <button className="nn-tool icon" onClick={() => setBitmap(b => flipped(b, true))} title="Peilaa vaakasuunnassa"><span className="material-symbols-outlined ui-icon">flip</span></button>
          <button className="nn-tool icon" onClick={() => setBitmap(b => flipped(b, false))} title="Peilaa pystysuunnassa"><span className="material-symbols-outlined ui-icon nn-rot90">flip</span></button>
        </div>
        <div className="nn-row">
          Koko
          <input type="number" className="nn-size" min={1} max={60} value={size.w} onChange={e => resizeTo(Number(e.target.value), size.h, 'w')} />
          ×
          <input type="number" className="nn-size" min={1} max={60} value={size.h} onChange={e => resizeTo(size.w, Number(e.target.value), 'h')} />
          <label className="nn-check" title="Pitää kuvasuhteen"><input type="checkbox" checked={lock} onChange={e => setLock(e.target.checked)} /> Lukitse</label>
        </div>
        <GridSizeInputs width={width} height={height} onResize={onResizeGrid}>
          <button className="btn-secondary" disabled={width === size.w && height === size.h} onClick={() => onResizeGrid(size.w, size.h)} title="Muuttaa ruudukon kuvan kokoiseksi">Sovita ruudukko kuvaan</button>
        </GridSizeInputs>
        <div className="nn-row">
          <button className="btn-secondary" disabled={!scaled} onClick={() => setSize({ w: bitmap.w, h: bitmap.h })}>Alkuperäinen koko</button>
          <button className="btn-secondary" onClick={() => setPos({ x: Math.floor((width - size.w) / 2), y: Math.floor((height - size.h) / 2) })}>Keskitä</button>
        </div>
        <div className="nn-row">
          <label className="nn-check" title="Täyttää ääriviivojen sisään jäävät alueet (F)"><input type="checkbox" checked={fill} onChange={e => setFill(e.target.checked)} /> Täytä muodot <kbd>F</kbd></label>
          <label className="nn-check" title="Vaihtaa täytetyt ja tyhjät ruudut kuvan alueella (I)"><input type="checkbox" checked={invert} onChange={e => setInvert(e.target.checked)} /> Käänteinen <kbd>I</kbd></label>
        </div>
        <TracerStatus analysis={analysis} />
        <TracerActions onCancel={onCancel} apply={apply} />
        <p className="nn-hint">Raahaa tai nuolet siirtävät ruutu kerrallaan (Shift = 5). + − muuttaa kokoa, ja pienennys ottaa lähimmän pikselin. Isompi kuva voi ylittää ruudukon, ylimenevä osa leikkautuu.</p>
      </div>
    </div>
  );
}

export default NonoPixelTracer;
